import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiMyAssessments } from '@/lib/phpApi';
import { useAuth } from '@/hooks/useAuth';
import { hasPhpBackend } from '@/lib/phpApi';
import {
  Beaker, ChevronRight, ChevronDown, FlaskConical, Calendar,
  Shield, AlertTriangle, CheckCircle2, X, FileText, BarChart3
} from 'lucide-react';

interface SavedAssessment {
  id: number;
  report_number: string;
  sample_name: string;
  standard: string;
  created_at: string;
  dosing_config?: any[];
  params?: Record<string, any>;
  result?: Record<string, any>;
  customer_id?: number;
}

export default function SavedAssessmentsDosing() {
  const { isAuthenticated } = useAuth();
  const [assessments, setAssessments] = useState<SavedAssessment[]>([]);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !hasPhpBackend()) {
      // Load from localStorage as fallback
      const local = JSON.parse(localStorage.getItem('rb_local_assessments') || '[]');
      setAssessments(local);
      return;
    }
    setLoading(true);
    apiMyAssessments()
      .then((data) => {
        const rows = data.results || [];
        setAssessments(rows);
        // Also cache locally
        localStorage.setItem('rb_local_assessments', JSON.stringify(rows));
      })
      .catch(() => {
        const local = JSON.parse(localStorage.getItem('rb_local_assessments') || '[]');
        setAssessments(local);
      })
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const formatDate = (d: string) => {
    try { return new Date(d).toLocaleDateString('en-GB'); } catch { return d; }
  };

  const getComplianceStatus = (result?: Record<string, any>) => {
    if (!result) return { label: 'Unknown', color: 'text-slate-400', icon: AlertTriangle };
    const summary = result.complianceSummary;
    if (!summary) return { label: 'Pending', color: 'text-amber-400', icon: AlertTriangle };
    if (summary.compliant === true || summary.overall === 'Compliant') {
      return { label: 'Compliant', color: 'text-emerald-400', icon: CheckCircle2 };
    }
    return { label: 'Review Required', color: 'text-amber-400', icon: AlertTriangle };
  };

  if (loading) {
    return (
      <div className="p-6 rb-inner-card">
        <div className="flex items-center gap-2 mb-4">
          <Beaker className="w-4 h-4 text-rb-brand" />
          <span className="text-sm font-semibold text-slate-200">Saved Assessments &amp; Dosing</span>
        </div>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-12 bg-slate-800/50 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (assessments.length === 0) {
    return (
      <div className="p-6 rb-inner-card">
        <div className="flex items-center gap-2 mb-3">
          <Beaker className="w-4 h-4 text-rb-brand" />
          <span className="text-sm font-semibold text-slate-200">Saved Assessments &amp; Dosing</span>
        </div>
        <div className="text-center py-6">
          <FlaskConical className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-xs text-slate-500">No saved assessments yet</p>
          <p className="text-[10px] text-slate-600 mt-1">Run an assessment and save it to see dosing details here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rb-inner-card">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800/50">
        <div className="flex items-center gap-2">
          <Beaker className="w-4 h-4 text-rb-brand" />
          <span className="text-sm font-semibold text-slate-200">Saved Assessments &amp; Dosing</span>
          <span className="px-1.5 py-0.5 rounded-full bg-rb-brand/10 text-rb-brand text-[10px] font-bold">
            {assessments.length}
          </span>
        </div>
      </div>

      <div className="divide-y divide-slate-800/50">
        {assessments.map((a) => {
          const status = getComplianceStatus(a.result);
          const StatusIcon = status.icon;
          const isExpanded = expandedId === a.id;
          const dosing = a.dosing_config || [];
          const activeDosing = dosing.filter((d: any) => d?.enabled);

          return (
            <div key={a.id} className="group">
              <button
                onClick={() => setExpandedId(isExpanded ? null : a.id)}
                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-800/30 transition-colors text-left"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <FileText className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                    <span className="text-xs font-semibold text-slate-200 truncate">
                      {a.report_number || a.sample_name || `Assessment #${a.id}`}
                    </span>
                    <StatusIcon className={`w-3 h-3 ${status.color} shrink-0`} />
                    <span className={`text-[10px] ${status.color}`}>{status.label}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px] text-slate-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(a.created_at)}
                    </span>
                    <span className="text-[10px] text-slate-500">{a.standard || 'UK_EA'}</span>
                    {activeDosing.length > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500/10 text-cyan-400">
                        {activeDosing.length} chemical{activeDosing.length > 1 ? 's' : ''} active
                      </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-slate-500 group-hover:text-slate-300 transition-colors">
                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </div>
              </button>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-4 pt-1 bg-slate-900/30">
                      {/* Water Parameters Summary */}
                      {a.params && (
                        <div className="mb-3">
                          <div className="flex items-center gap-1.5 mb-2">
                            <BarChart3 className="w-3 h-3 text-slate-500" />
                            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Water Parameters</span>
                          </div>
                          <div className="grid grid-cols-4 gap-2">
                            {['pH', 'COD', 'BOD', 'TSS', 'TDS', 'O&G', 'NH3N', 'TP'].map((param) => {
                              const val = a.params?.[param];
                              if (val === undefined || val === null) return null;
                              return (
                                <div key={param} className="px-2 py-1 rounded bg-slate-800/50 border border-slate-800/30">
                                  <span className="text-[10px] text-slate-500">{param}</span>
                                  <div className="text-[11px] text-slate-200 font-mono">{typeof val === 'number' ? val.toFixed(1) : val}</div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Dosing Details */}
                      {dosing.length > 0 ? (
                        <div>
                          <div className="flex items-center gap-1.5 mb-2">
                            <FlaskConical className="w-3 h-3 text-rb-brand" />
                            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Dosing Arrangements</span>
                          </div>
                          <div className="space-y-1.5">
                            {dosing.map((d: any, idx: number) => (
                              <div
                                key={idx}
                                className={`flex items-center justify-between px-3 py-2 rounded-lg border ${
                                  d?.enabled
                                    ? 'bg-cyan-500/5 border-cyan-500/20'
                                    : 'bg-slate-800/30 border-slate-800/30 opacity-50'
                                }`}
                              >
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-2.5 h-2.5 rounded-full"
                                    style={{ backgroundColor: d?.color || 'var(--rb-text-3)' }}
                                  />
                                  <span className="text-[11px] text-slate-300">{d?.name || `Chemical ${idx + 1}`}</span>
                                  {d?.enabled && (
                                    <span className="px-1 py-0.5 rounded text-[10px] bg-emerald-500/10 text-emerald-400 font-medium">Active</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-3">
                                  {d?.dosage !== undefined && (
                                    <span className="text-[11px] text-slate-300 font-mono">{typeof d.dosage === 'number' ? d.dosage.toFixed(2) : d.dosage} <span className="text-slate-500">mg/L</span></span>
                                  )}
                                  {d?.cost !== undefined && (
                                    <span className="text-[10px] text-slate-500">£{typeof d.cost === 'number' ? d.cost.toFixed(2) : d.cost}/day</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-3">
                          <p className="text-[11px] text-slate-500">No dosing configuration recorded for this assessment</p>
                        </div>
                      )}

                      {/* Compliance Note */}
                      <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/30 border border-slate-800/30">
                        <Shield className={`w-3.5 h-3.5 ${status.color}`} />
                        <span className="text-[11px] text-slate-400">
                          {status.label === 'Compliant'
                            ? 'This assessment meets all discharge requirements for the selected standard.'
                            : status.label === 'Review Required'
                            ? 'Some parameters may not meet discharge requirements. Review dosing adjustments.'
                            : 'Run the assessment to determine compliance status.'}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
